import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Check, Sparkles, Truck, ChevronLeft, ChevronRight, Calendar } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAuth, API } from '@/App';
import { toast } from 'sonner';

// Updated subscription plans with correct pricing
const SUBSCRIPTION_PLANS = {
  'afternoon-tea': {
    name: 'Afternoon Tea Subscription',
    introPrice: 60, // First 3 months
    monthlyPrice: 27.50,
    introMonths: 3,
    description: 'Monthly delivery of our signature afternoon tea collection - unique treats you won\'t find anywhere else',
    features: [
      'Exclusive monthly afternoon tea box',
      'Track 24 next-day Royal Mail delivery',
      'Unique seasonal specialties',
      'Handcrafted artisan treats',
      '15% goes to charity'
    ],
    highlight: 'First 3 months for £60'
  },
  'cakery': {
    name: 'Cakery Subscription',
    introPrice: 45, // First 3 months
    monthlyPrice: 20,
    introMonths: 3,
    description: 'Monthly selection of artisan cakes and desserts delivered to your door',
    features: [
      'Monthly cakery box delivery',
      'Track 24 next-day Royal Mail delivery',
      'Handcrafted artisan desserts',
      'Seasonal flavours & surprises',
      '15% goes to charity'
    ],
    highlight: 'First 3 months for £45'
  }
};

// 6-month preview content for each subscription
const getMonthlyPreview = (planId) => {
  const currentMonth = new Date().getMonth();
  const months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
  
  const afternoonTeaThemes = [
    { month: 'January', theme: 'Winter Wonderland', items: 'Warming spiced scones, gingerbread treats, hot chocolate truffles' },
    { month: 'February', theme: 'Valentine\'s Collection', items: 'Rose macarons, heart-shaped shortbread, raspberry delights' },
    { month: 'March', theme: 'Spring Awakening', items: 'Lemon drizzle, violet cream puffs, primrose biscuits' },
    { month: 'April', theme: 'Easter Special', items: 'Hot cross scones, Easter egg brownies, spring florals' },
    { month: 'May', theme: 'Garden Party', items: 'Elderflower treats, lavender shortbread, cucumber sandwiches' },
    { month: 'June', theme: 'Summer Berries', items: 'Strawberry tarts, blueberry scones, cream delights' },
    { month: 'July', theme: 'Tropical Paradise', items: 'Passion fruit eclairs, coconut macarons, mango treats' },
    { month: 'August', theme: 'Heritage Collection', items: 'Victoria sponge, Battenberg, classic scones' },
    { month: 'September', theme: 'Autumn Harvest', items: 'Apple & cinnamon, blackberry tarts, toffee treats' },
    { month: 'October', theme: 'Spooky Season', items: 'Pumpkin spice, caramel apples, chocolate bats' },
    { month: 'November', theme: 'Bonfire Night', items: 'Treacle tarts, parkin, toffee puddings' },
    { month: 'December', theme: 'Festive Cheer', items: 'Mince pies, Christmas pudding truffles, mulled wine treats' }
  ];

  const cakeryThemes = [
    { month: 'January', theme: 'New Year Indulgence', items: 'Chocolate torte, salted caramel brownies, fudge cake' },
    { month: 'February', theme: 'Love is Sweet', items: 'Red velvet cupcakes, chocolate hearts, cherry bakewells' },
    { month: 'March', theme: 'Spring Florals', items: 'Lemon cake, flower cupcakes, honey madeleines' },
    { month: 'April', theme: 'Easter Treats', items: 'Simnel cake, Easter cupcakes, chocolate nests' },
    { month: 'May', theme: 'Afternoon Delights', items: 'Fruit tarts, éclairs, cream horns' },
    { month: 'June', theme: 'Summer Celebration', items: 'Berry pavlova, fruit cake, summerfruit tarts' },
    { month: 'July', theme: 'Picnic Perfect', items: 'Mini loaf cakes, fruit slices, iced buns' },
    { month: 'August', theme: 'Classic British', items: 'Dundee cake, Chelsea buns, Eccles cakes' },
    { month: 'September', theme: 'Apple Season', items: 'Apple cake, cinnamon rolls, crumble bars' },
    { month: 'October', theme: 'Autumn Warmers', items: 'Sticky toffee, ginger cake, pear tarts' },
    { month: 'November', theme: 'Cosy Comforts', items: 'Coffee cake, walnut brownies, caramel slices' },
    { month: 'December', theme: 'Christmas Magic', items: 'Christmas cake, Yule log, mince pie brownies' }
  ];

  const themes = planId === 'afternoon-tea' ? afternoonTeaThemes : cakeryThemes;
  
  // Get 6 months starting from current month
  const preview = [];
  for (let i = 0; i < 6; i++) {
    const monthIndex = (currentMonth + i) % 12;
    preview.push(themes[monthIndex]);
  }
  return preview;
};

// Placeholder images for subscription boxes
const getBoxImage = (planId, monthIndex) => {
  // Using seasonal placeholder images
  const season = monthIndex % 4;
  const baseImages = {
    'afternoon-tea': [
      'https://images.unsplash.com/photo-1558961363-fa8fdf82db35?w=400&h=300&fit=crop', // Tea & scones
      'https://images.unsplash.com/photo-1563729784474-d77dbb933a9e?w=400&h=300&fit=crop', // Macarons
      'https://images.unsplash.com/photo-1486427944299-d1955d23e34d?w=400&h=300&fit=crop', // Cupcakes
      'https://images.unsplash.com/photo-1519915028121-7d3463d20b13?w=400&h=300&fit=crop'  // Tea set
    ],
    'cakery': [
      'https://images.unsplash.com/photo-1578985545062-69928b1d9587?w=400&h=300&fit=crop', // Chocolate cake
      'https://images.unsplash.com/photo-1571115177098-24ec42ed204d?w=400&h=300&fit=crop', // Brownies
      'https://images.unsplash.com/photo-1587668178277-295251f900ce?w=400&h=300&fit=crop', // Pastries
      'https://images.unsplash.com/photo-1464349095431-e9a21285b5f3?w=400&h=300&fit=crop'  // Layer cake
    ]
  };
  return baseImages[planId][season];
};

const SubscriptionsPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [mySubscriptions, setMySubscriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [subscribing, setSubscribing] = useState(null);
  const [currentSlide, setCurrentSlide] = useState({ 'afternoon-tea': 0, 'cakery': 0 });

  useEffect(() => {
    fetchSubscriptions();
  }, [user]);

  const fetchSubscriptions = async () => {
    try {
      if (user) {
        const token = localStorage.getItem('session_token');
        const subsRes = await fetch(`${API}/subscriptions/my`, {
          headers: { 'Authorization': `Bearer ${token}` },
          credentials: 'include'
        });
        if (subsRes.ok) {
          setMySubscriptions(await subsRes.json());
        }
      }
    } catch (error) {
      console.error('Failed to fetch subscriptions:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubscribe = async (planId) => {
    if (!user) {
      toast.error('Please login to subscribe');
      navigate('/login');
      return;
    }

    setSubscribing(planId);
    try {
      const token = localStorage.getItem('session_token');
      const response = await fetch(`${API}/subscriptions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        credentials: 'include',
        body: JSON.stringify({
          plan: planId,
          origin_url: window.location.origin
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to create subscription');
      }

      const data = await response.json();
      window.location.href = data.checkout_url;
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSubscribing(null);
    }
  };

  const handleCancelSubscription = async (subscriptionId) => {
    if (!window.confirm('Are you sure you want to cancel this subscription?')) return;

    try {
      const token = localStorage.getItem('session_token');
      const response = await fetch(`${API}/subscriptions/${subscriptionId}/cancel`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        credentials: 'include'
      });

      if (response.ok) {
        toast.success('Subscription cancelled');
        fetchSubscriptions();
      } else {
        throw new Error('Failed to cancel subscription');
      }
    } catch (error) {
      toast.error(error.message);
    }
  };

  const nextSlide = (planId) => {
    setCurrentSlide(prev => ({
      ...prev,
      [planId]: Math.min(prev[planId] + 1, 5)
    }));
  };

  const prevSlide = (planId) => {
    setCurrentSlide(prev => ({
      ...prev,
      [planId]: Math.max(prev[planId] - 1, 0)
    }));
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background pt-16">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-text-secondary font-body">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background pt-16" data-testid="subscriptions-page">
      {/* Hero Section */}
      <section className="relative overflow-hidden hero-gradient py-20">
        <div className="absolute top-20 right-10 w-96 h-96 bg-primary/10 rounded-full blur-3xl"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
            <Sparkles className="w-12 h-12 text-primary mx-auto mb-6 animate-float" />
            <h1 className="text-5xl font-heading font-bold text-white mb-4" data-testid="subscriptions-title">
              Monthly Subscriptions
            </h1>
            <p className="text-lg text-text-secondary max-w-2xl mx-auto font-body">
              Unique artisan treats delivered monthly. See what's coming in each box below!
            </p>
          </motion.div>
        </div>
      </section>

      {/* Track 24 Info Banner */}
      <section className="py-4 bg-primary/10 border-y border-primary/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-center gap-3 text-center">
            <Truck className="w-5 h-5 text-primary" />
            <p className="text-white font-body">
              All subscriptions include <span className="text-primary font-bold">Track 24</span> — Next day Royal Mail delivery
            </p>
          </div>
        </div>
      </section>

      {/* Subscription Plans with Preview Sliders */}
      <section className="py-20 bg-background-elevated">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 space-y-16">
          {Object.entries(SUBSCRIPTION_PLANS).map(([planId, plan]) => {
            const monthlyPreview = getMonthlyPreview(planId);
            const currentIndex = currentSlide[planId];
            
            return (
              <motion.div
                key={planId}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                viewport={{ once: true }}
                className="bg-background-paper rounded-3xl p-8 border border-primary/20"
                data-testid={`subscription-plan-${planId}`}
              >
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                  {/* Plan Details */}
                  <div>
                    <div className="inline-block bg-primary text-white text-sm font-bold py-2 px-4 rounded-full mb-4">
                      {plan.highlight}
                    </div>
                    <h2 className="text-3xl font-heading font-bold text-white mb-3">{plan.name}</h2>
                    <p className="text-text-secondary font-body mb-6">{plan.description}</p>
                    
                    {/* Pricing */}
                    <div className="bg-background-elevated rounded-xl p-6 mb-6">
                      <div className="flex items-baseline gap-2 mb-2">
                        <span className="text-4xl font-accent text-primary">£{plan.introPrice}</span>
                        <span className="text-text-muted font-body">for first {plan.introMonths} months</span>
                      </div>
                      <p className="text-text-secondary text-sm">
                        Then £{plan.monthlyPrice.toFixed(2)}/month after (inc. shipping)
                      </p>
                    </div>

                    {/* Features */}
                    <ul className="space-y-3 mb-6">
                      {plan.features.map((feature, i) => (
                        <li key={i} className="flex items-start">
                          <Check className="w-5 h-5 text-primary mr-3 flex-shrink-0 mt-0.5" />
                          <span className="text-text-secondary font-body text-sm">{feature}</span>
                        </li>
                      ))}
                    </ul>

                    <Button
                      onClick={() => handleSubscribe(planId)}
                      disabled={subscribing === planId}
                      className="w-full bg-primary hover:bg-primary-hover text-white rounded-full py-4 text-lg font-semibold shadow-lg shadow-primary/30"
                      data-testid={`subscribe-${planId}-btn`}
                    >
                      {subscribing === planId ? 'Processing...' : 'Subscribe Now'}
                    </Button>
                  </div>

                  {/* 6-Month Preview Slider */}
                  <div>
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-xl font-heading font-semibold text-white flex items-center gap-2">
                        <Calendar className="w-5 h-5 text-primary" />
                        What's in Your Box
                      </h3>
                      <div className="flex gap-2">
                        <button
                          onClick={() => prevSlide(planId)}
                          disabled={currentIndex === 0}
                          className={`p-2 rounded-full ${currentIndex === 0 ? 'bg-white/5 text-white/30' : 'bg-primary/20 text-primary hover:bg-primary/30'}`}
                          data-testid={`prev-slide-${planId}`}
                        >
                          <ChevronLeft className="w-5 h-5" />
                        </button>
                        <button
                          onClick={() => nextSlide(planId)}
                          disabled={currentIndex === 5}
                          className={`p-2 rounded-full ${currentIndex === 5 ? 'bg-white/5 text-white/30' : 'bg-primary/20 text-primary hover:bg-primary/30'}`}
                          data-testid={`next-slide-${planId}`}
                        >
                          <ChevronRight className="w-5 h-5" />
                        </button>
                      </div>
                    </div>

                    {/* Preview Card */}
                    <div className="relative overflow-hidden rounded-2xl bg-background-elevated border border-primary/10">
                      <div 
                        className="flex transition-transform duration-300"
                        style={{ transform: `translateX(-${currentIndex * 100}%)` }}
                      >
                        {monthlyPreview.map((preview, idx) => (
                          <div key={idx} className="w-full flex-shrink-0 p-6">
                            <div className="aspect-video rounded-xl overflow-hidden mb-4">
                              <img
                                src={getBoxImage(planId, idx)}
                                alt={`${preview.month} ${plan.name}`}
                                className="w-full h-full object-cover"
                              />
                            </div>
                            <div className="text-center">
                              <span className="text-primary font-semibold text-sm">{preview.month}</span>
                              <h4 className="text-xl font-heading font-bold text-white mt-1">{preview.theme}</h4>
                              <p className="text-text-secondary text-sm mt-2">{preview.items}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Slide indicators */}
                    <div className="flex justify-center gap-2 mt-4">
                      {monthlyPreview.map((_, idx) => (
                        <button
                          key={idx}
                          onClick={() => setCurrentSlide(prev => ({ ...prev, [planId]: idx }))}
                          className={`w-2 h-2 rounded-full transition-all ${
                            idx === currentIndex ? 'bg-primary w-6' : 'bg-white/20 hover:bg-white/40'
                          }`}
                        />
                      ))}
                    </div>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </section>

      {/* My Subscriptions */}
      {user && mySubscriptions.length > 0 && (
        <section className="py-20 bg-background">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-heading font-bold text-white mb-8">My Subscriptions</h2>
            <div className="space-y-4">
              {mySubscriptions.map(sub => (
                <motion.div
                  key={sub.subscription_id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-background-paper rounded-2xl p-6 border border-primary/20"
                  data-testid={`my-subscription-${sub.subscription_id}`}
                >
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div>
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="text-xl font-heading font-semibold text-white capitalize">
                          {sub.plan.replace('-', ' ')} Plan
                        </h3>
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                          sub.status === 'active' 
                            ? 'bg-green-500/20 text-green-400' 
                            : sub.status === 'pending' 
                            ? 'bg-yellow-500/20 text-yellow-400' 
                            : 'bg-white/10 text-text-muted'
                        }`}>
                          {sub.status}
                        </span>
                      </div>
                      <div className="flex flex-wrap gap-4 text-sm text-text-secondary">
                        <span>£{sub.price.toFixed(2)}</span>
                        <span>Started: {new Date(sub.start_date).toLocaleDateString()}</span>
                        {sub.status === 'active' && (
                          <span>Next delivery: {new Date(sub.next_billing_date).toLocaleDateString()}</span>
                        )}
                      </div>
                    </div>
                    {sub.status === 'active' && (
                      <Button
                        onClick={() => handleCancelSubscription(sub.subscription_id)}
                        variant="outline"
                        className="border-red-500/50 text-red-400 hover:bg-red-500/10 rounded-full"
                        data-testid={`cancel-subscription-${sub.subscription_id}`}
                      >
                        Cancel
                      </Button>
                    )}
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* How It Works */}
      <section className="py-20 bg-background">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-heading font-bold text-white mb-12">How It Works</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-background-paper rounded-2xl p-6 border border-primary/20">
              <div className="w-12 h-12 bg-primary/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-primary font-bold text-xl">1</span>
              </div>
              <h3 className="text-lg font-heading font-semibold text-white mb-2">Choose Your Plan</h3>
              <p className="text-text-secondary font-body text-sm">Select Afternoon Tea or Cakery subscription</p>
            </div>
            <div className="bg-background-paper rounded-2xl p-6 border border-primary/20">
              <div className="w-12 h-12 bg-primary/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-primary font-bold text-xl">2</span>
              </div>
              <h3 className="text-lg font-heading font-semibold text-white mb-2">We Prepare</h3>
              <p className="text-text-secondary font-body text-sm">Our chefs handcraft your monthly treats</p>
            </div>
            <div className="bg-background-paper rounded-2xl p-6 border border-primary/20">
              <div className="w-12 h-12 bg-primary/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-primary font-bold text-xl">3</span>
              </div>
              <h3 className="text-lg font-heading font-semibold text-white mb-2">Enjoy!</h3>
              <p className="text-text-secondary font-body text-sm">Delivered to your door via Track 24</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default SubscriptionsPage;
